import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { trackPurchase } from "@/lib/meta-pixel";

// Efter att Stripe redirectat tillbaka pollar vi vår backend tills webhooken
// har flippat organisationens status till `trialing`. I sandbox kan detta ta
// någon sekund. Vi ger upp efter ~30 s och skickar användaren till /billing
// där hen kan starta om ifall något gick fel.
export default function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refreshSubscription } = useAuth();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      for (let i = 0; i < 15 && !cancelled; i++) {
        const sub = await refreshSubscription();
        if (sub && ["trialing", "active"].includes(sub.subscription_status)) {
          // Meta Pixel Purchase — trigga endast en gång per checkout-session
          // (session_id från query är unik per Stripe-checkout). Om värdet
          // saknas använder vi ett fallback så eventet ändå skickas.
          trackPurchase({
            content_name: "subscription_started",
            currency: sub.currency || "SEK",
            value: sub.amount || 0,
          });
          toast.success("Prövoperioden är igång — välkommen!");
          // Nya admins skickas till /welcome för att bjuda in teamet.
          // Andra roller / admins med befintligt team → direkt till appen.
          let goWelcome = false;
          if (user?.role === "admin") {
            try {
              const r = await api.get("/users");
              const others = (r.data || []).filter(
                (u) => u.user_id !== user.user_id && u.active
              );
              goWelcome = others.length === 0;
            } catch {
              goWelcome = true; // vid fel — visa welcome, admin kan skippa
            }
          }
          navigate(goWelcome ? "/welcome" : "/", { replace: true });
          return;
        }
        setTick(i + 1);
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) {
        toast.info("Betalningen bekräftas fortfarande — kolla fakturasidan.");
        navigate("/billing", { replace: true });
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [refreshSubscription, navigate, user]);

  return (
    <div
      data-testid="billing-success-page"
      className="min-h-screen flex items-center justify-center bg-app"
    >
      <div className="bg-surface border border-app rounded-2xl p-10 max-w-md w-full mx-4 text-center">
        <h1 className="font-heading text-3xl text-text-primary">
          Aktiverar ditt konto…
        </h1>
        <p className="mt-4 text-text-muted text-sm">
          Vi bekräftar betalningen med Stripe. Det tar bara några sekunder.
        </p>
        <p className="mt-6 text-xs text-text-muted">
          Session: {params.get("session_id")?.slice(0, 20)}…
        </p>
        <div className="mt-4 text-xs text-text-muted">Kontroll {tick}/15…</div>
      </div>
    </div>
  );
}
